import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Wallet, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutocompleteInput } from '../components/ui/autocomplete-input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { INDIAN_STATES, MAJOR_INDIAN_CITIES } from '../lib/constants';
import { useApp } from '../context/AppContext';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Checkout() {
  const { fetchCartCount } = useApp();
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [settings, setSettings] = useState({ razorpay_enabled: true });
  const [loading, setLoading] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: ''
  });
  const [processing, setProcessing] = useState(false);
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
    fetchSettings();
    loadRazorpayScript();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      if (shippingAddress.pincode.length === 6) {
        setLoadingPincode(true);
        try {
          const response = await axios.get(`https://api.postalpincode.in/pincode/${shippingAddress.pincode}`);
          if (response.data[0].Status === 'Success') {
            const { District, State } = response.data[0].PostOffice[0];
            setShippingAddress(prev => ({
              ...prev,
              city: District,
              state: State
            }));
          }
        } catch (error) {
          console.error('Failed to fetch location from pincode:', error);
        } finally {
          setLoadingPincode(false);
        }
      }
    };
    fetchLocation();
  }, [shippingAddress.pincode]);

  const fetchUserProfile = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const profile = response.data;
      if (profile) {
        setUserCredits(profile.credits || 0);
        setShippingAddress(prev => {
          const newAddress = { ...prev };
          if (profile.name) newAddress.fullName = profile.name;
          if (profile.phone) newAddress.phone = profile.phone;
          
          if (profile.address) {
            if (typeof profile.address === 'object') {
              // If it's the full object we saved earlier
              Object.assign(newAddress, profile.address);
            } else {
              // If it's just the street address string
              newAddress.address = profile.address;
            }
          }
          return newAddress;
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      if (!response.data.razorpay_enabled) {
        setPaymentMethod('cod');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchCart = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      
      const productDetails = {};
      for (const item of response.data.items || []) {
        const prod = await axios.get(`${API}/products/${item.product_id}`);
        productDetails[item.product_id] = prod.data;
      }
      setProducts(productDetails);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveUserProfile = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    try {
      await axios.put(
        `${API}/profile`,
        {
          phone: shippingAddress.phone,
          address: shippingAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to auto-save profile details:', error);
    }
  };

  const handleRazorpayPayment = async (razorpayOrderId, backendOrderId, amount) => {
    const token = sessionStorage.getItem('token');
    const user = JSON.parse(sessionStorage.getItem('user'));

    const options = {
      key: settings.razorpay_key_id,
      amount: amount * 100,
      currency: 'INR',
      name: 'Ved3dCreations',
      description: '3D Printed Products',
      order_id: razorpayOrderId,
      handler: async function (response) {
        try {
          await axios.put(
            `${API}/orders/${backendOrderId}/payment`,
            {
              payment_status: 'paid',
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await saveUserProfile();
          await fetchCartCount();
          toast.success('Order placed successfully!');
          navigate('/orders');
        } catch (error) {
          toast.error('Payment recorded but failed to update status');
          navigate('/orders');
        }
      },
      modal: {
        ondismiss: async function() {
          try {
            await axios.put(
              `${API}/orders/${backendOrderId}/payment`,
              { payment_status: 'payment failed' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Failed to update payment status to failed');
          }
          setProcessing(false);
          toast.error('Payment cancelled');
        }
      },
      prefill: {
        name: user.name,
        email: user.email,
        contact: shippingAddress.phone
      },
      theme: {
        color: '#8b5cf6'
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on('payment.failed', async function (response) {
      try {
        await axios.put(
          `${API}/orders/${backendOrderId}/payment`,
          { payment_status: 'payment failed' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to update payment status to failed');
      }
      toast.error('Payment failed');
    });
    paymentObject.open();
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (processing) return;

    if (!shippingAddress.fullName || !shippingAddress.address || !shippingAddress.city || !shippingAddress.pincode || !shippingAddress.phone) {
      toast.error('Please fill all shipping details');
      return;
    }

    setProcessing(true);
    const token = sessionStorage.getItem('token');
    const totalAmount = total;

    try {
      // Create backend order first (pending for Razorpay, Cash on Delivery for COD)
      const backendOrderResponse = await axios.post(
        `${API}/orders`,
        {
          items: cart.items,
          total_amount: total,
          shipping_address: shippingAddress,
          payment_method: paymentMethod,
          credits_used: creditsToApply
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const backendOrderId = backendOrderResponse.data.id;

      if (paymentMethod === 'razorpay') {
        const razorpayOrderResponse = await axios.post(
          `${API}/orders/create-razorpay-order`,
          { 
            amount: total,
            items: cart.items,
            shipping_address: shippingAddress,
            credits_used: creditsToApply
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await handleRazorpayPayment(razorpayOrderResponse.data.id, backendOrderId, total);
      } else {
        await saveUserProfile();
        await fetchCartCount();
        toast.success('Order placed successfully!');
        navigate('/orders');
      }
    } catch (error) {
      toast.error('Failed to place order');
      setProcessing(false);
    }
  };

  const subtotal = cart.items?.reduce((total, item) => {
    const product = products[item.product_id];
    return total + (product?.price || 0) * item.quantity;
  }, 0) || 0;
  
  const isBangalore = shippingAddress.city?.toLowerCase().includes('bangalore') || shippingAddress.city?.toLowerCase().includes('bengaluru');
  const shippingLimit = isBangalore ? 500 : 1500;
  const shipping = subtotal >= shippingLimit ? 0 : 150;
  const totalBeforeCredits = subtotal + shipping;
  const creditsToApply = useCredits ? Math.min(userCredits, totalBeforeCredits) : 0;
  const total = totalBeforeCredits - creditsToApply;

  if (loading) {
    return (
      <PageLoadingState title="Loading checkout" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate('/cart')} 
            className="mb-6 -ml-2 text-muted-foreground hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Cart
          </Button>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-12" data-testid="checkout-title">Checkout</h1>

          <form onSubmit={handlePlaceOrder}>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-morphic rounded-2xl p-6" data-testid="shipping-form">
                  <h2 className="text-2xl font-semibold mb-6">Shipping Address</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input
                        id="fullName"
                        value={shippingAddress.fullName}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, fullName: e.target.value })}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="fullname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={shippingAddress.phone}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="phone-input"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={shippingAddress.address}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <AutocompleteInput
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        suggestions={MAJOR_INDIAN_CITIES}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <AutocompleteInput
                        id="state"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                        suggestions={INDIAN_STATES}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pincode">Pincode</Label>
                      <div className="relative">
                        <Input
                          id="pincode"
                          value={shippingAddress.pincode}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, pincode: e.target.value })}
                          className="bg-white/5 border-white/10 pr-10"
                          required
                          data-testid="pincode-input"
                          maxLength={6}
                        />
                        {loadingPincode && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="glass-morphic rounded-2xl p-6" data-testid="payment-method">
                  <h2 className="text-2xl font-semibold mb-6">Payment Method</h2>
                  
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    {settings.razorpay_enabled && (
                      <div className="flex items-center space-x-3 p-4 rounded-lg border border-white/10 hover:border-primary/50 transition-colors duration-300" data-testid="razorpay-option">
                        <RadioGroupItem value="razorpay" id="razorpay" />
                        <Label htmlFor="razorpay" className="flex items-center gap-3 cursor-pointer flex-1">
                          <CreditCard className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Online Payment (Razorpay)</p>
                            <p className="text-sm text-muted-foreground">Pay securely with card, UPI, or netbanking</p>
                          </div>
                        </Label>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-3 p-4 rounded-lg border border-white/10 hover:border-primary/50 transition-colors duration-300" data-testid="cod-option">
                      <RadioGroupItem value="cod" id="cod" />
                      <Label htmlFor="cod" className="flex items-center gap-3 cursor-pointer flex-1">
                        <Wallet className="h-5 w-5 text-accent" />
                        <div>
                          <p className="font-medium">Cash on Delivery</p>
                          <p className="text-sm text-muted-foreground">Pay when you receive your order</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="glass-morphic rounded-2xl p-6 sticky top-32" data-testid="order-summary">
                  <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
                  
                  {/* Credits Usage */}
                  {userCredits > 0 && (
                    <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">Use Credits</span>
                        </div>
                        <span className="text-sm font-bold text-primary">₹{userCredits.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="useCredits"
                          checked={useCredits}
                          onChange={(e) => setUseCredits(e.target.checked)}
                          className="h-4 w-4 rounded border-white/10 bg-white/5 text-primary focus:ring-primary cursor-pointer"
                        />
                        <Label htmlFor="useCredits" className="text-xs text-muted-foreground cursor-pointer">
                          Apply credits to this order
                        </Label>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4 mb-6">
                    {cart.items?.map((item) => {
                      const product = products[item.product_id];
                      if (!product) return null;
                      return (
                        <div key={item.product_id} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{product.name} x{item.quantity}</span>
                          <span>₹{(product.price * item.quantity).toFixed(2)}</span>
                        </div>
                      );
                    })}
                    
                    <div className="pt-4 space-y-2 border-t border-white/10">
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Subtotal</span>
                        <span>₹{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Shipping</span>
                        <span>{shipping === 0 ? <span className="text-accent font-semibold">FREE</span> : `₹${shipping.toFixed(2)}`}</span>
                      </div>
                      {creditsToApply > 0 && (
                        <div className="flex justify-between text-sm text-primary font-medium">
                          <span>Credits Applied</span>
                          <span>-₹{creditsToApply.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-2">
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                        Free delivery in Bangalore above ₹500
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                        Free delivery rest of India above ₹1500
                      </p>
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <div className="flex justify-between text-xl font-semibold">
                        <span>Total</span>
                        <span className="text-primary" data-testid="checkout-total">₹{total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300"
                    data-testid="place-order-button"
                  >
                    {processing ? 'Processing...' : 'Place Order'}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
